/**
 * Figma Node IDs:
 * Design Frame: 385:19706
 * Instance IDs: I1497:75191, I1497:76407, I1497:76682, I1497:77380, I385:19709, I1497:75904
 * Composite: true
 * Last Updated: 2025-01-15
 * Generated by: Claude Code figma-component command
 */
import * as React from "react";
import { useState, useCallback } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useIntl, defineMessages } from "react-intl";
import TranslationsProvider from "@learningblocks/simulations/shared-components/lib/TranslationsProvider";
import { getDir } from "@learningblocks/shared-components/lib/Translations";

import { CircleX, ChevronLeft, ChevronRight } from "@labxchange/icons";

import { LxButton } from "../LxButton";
import { ButtonType } from "../LxButton/constants";
import { fetchTranslations } from "./locales/config";
import * as styles from "./Modal.css";

const messages = defineMessages({
  close: {
    id: "modal.close",
    defaultMessage: "Close",
    description: "Close button text for modal",
  },
  backTo: {
    id: "modal.backTo",
    defaultMessage: "Back to {label}",
    description: "Accessible label for the fullscreen modal back button",
  },
});

export type ModalSize = "small" | "medium" | "large";

/** Presentation variant for the modal overlay. */
export type ModalVariant = "default" | "fullscreen";

export interface ModalProps {
  /** Content to render inside the modal */
  children?: React.ReactNode;
  /**
   * Fullscreen-only: an array of design-system components rendered stacked
   * inside the modal's content column. Used by the "fullscreen" variant so a
   * caller can compose the overlay from any set of DS components (e.g. a
   * SpecialTextBox above a question) without a bespoke wrapper. Ignored by the
   * default variant, which renders `children`. Each entry needs a stable
   * React `key`.
   */
  components?: React.ReactNode[];

  /** Controls whether the modal is open (controlled mode). If not provided, modal manages its own state. */
  isOpen?: boolean;
  /** Optional callback fired before the modal closes */
  onClose?: () => void;
  /** Additional CSS class for the modal container */
  className?: string;
  /** Test identifier for automated testing */
  testId?: string;
  /** Accessible label for the modal dialog */
  ariaLabel?: string;
  /** ID of element describing the modal content */
  ariaDescribedBy?: string;

  /** Custom translation message ID for close button text (default: "modal.close") */
  closeButtonText?: string;
  /** Whether to show the close button (default: true) */
  showCloseButton?: boolean;
  /**
   * Size variant controlling the content column's max-width (default: "medium").
   * Applies in both variants: in "fullscreen" it caps the centred column on the
   * gradient overlay (the overlay stays edge-to-edge; the content does not).
   */
  size?: ModalSize;
  /**
   * Render modal inline without portal (for documentation/preview purposes).
   * The fullscreen back button is a fixed-position overlay affordance that has
   * no meaning without the portal, so inline fullscreen falls back to the
   * in-flow close button — the modal always keeps a visible dismiss control.
   */
  inline?: boolean;
  /** Variant for the close button (default: "oncolor") */
  closeButtonVariant?: ButtonType;
  /**
   * Override the overlay's stacking order (default: the `overlay` style's
   * z-index of 1000). Use when the modal must sit below another fixed layer —
   * e.g. protocol-sim modals lower this so the glossary definition popup
   * (z-index 601) renders on top, matching the legacy modal behaviour.
   */
  overlayZIndex?: number;
  /**
   * When true, the dialog does not close on outside interaction (Radix's
   * `onInteractOutside` is prevented); it closes only via the close button or
   * Escape. Use when a sibling overlay must stay usable over the modal — e.g.
   * the glossary definition popup in protocol-sim modals, so clicking it does
   * not dismiss the modal. Default false (clicking outside closes the modal).
   */
  disableOutsideDismiss?: boolean;
  /**
   * Presentation variant. "fullscreen" renders a full-viewport blue-to-navy
   * gradient overlay (the `color.gradient.medium` token), matching the legacy
   * psim FullscreenModal look. Note this describes the *overlay* only: the
   * content is still a centred column whose max-width comes from `size`
   * (default "medium" = 700px), not edge-to-edge. Widen it via `size`; the
   * caller owns the inner card. Default "default".
   */
  variant?: ModalVariant;
  /**
   * When set, renders a "← {backButtonLabel}" button at the top-left of the
   * overlay (fullscreen variant) instead of the default close (×) button.
   * The button closes the modal when clicked.
   */
  backButtonLabel?: string;
}

const ModalInternal: React.FC<ModalProps> = ({
  children,
  components,
  isOpen: isOpenProp,
  onClose,
  className,
  testId,
  ariaLabel,
  ariaDescribedBy,
  closeButtonText,
  showCloseButton = true,
  size = "medium",
  inline = false,
  closeButtonVariant = "oncolor",
  overlayZIndex,
  disableOutsideDismiss = false,
  variant = "default",
  backButtonLabel,
}) => {
  const intl = useIntl();
  const isRtl = getDir(intl.locale) === "rtl";
  const isControlled = isOpenProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(true);
  const isOpen = isControlled ? isOpenProp : internalOpen;

  const handleClose = useCallback(() => {
    onClose?.();
    if (!isControlled) {
      setInternalOpen(false);
    }
  }, [onClose, isControlled]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) handleClose();
    },
    [handleClose]
  );

  const isFullscreen = variant === "fullscreen";

  // `components` is a fullscreen-only affordance: an array of DS components
  // stacked in the content column. Every other variant ignores it and renders
  // `children` exactly as before, so nothing else is disturbed.
  const content = isFullscreen && components ? components : children;

  const combinedClassName = [
    isFullscreen ? styles.fullscreenModalContainer : styles.modalContainer,
    styles.sizeVariants[size],
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // In fullscreen mode the back button is the dismiss affordance and replaces the
  // close button — but only when a backButtonLabel was actually provided. Without
  // one we keep the close button (below) so the modal is never a dead-end with no
  // visible way out (Escape only).
  //
  // The back button is pinned to the viewport with `position: fixed` (an overlay
  // affordance), which is meaningless in the portal-less inline preview, so inline
  // fullscreen intentionally falls back to the in-flow close button below.
  const showBackButton = isFullscreen && !!backButtonLabel && !inline;

  // The back button is rendered inside Dialog.Content (below) so it sits within
  // Radix's focus trap and is keyboard-reachable, and does not register as an
  // outside interaction. It reuses the design-system LxButton (`oncolor` =
  // white-on-blue, matching the close button) so padding, radius, font and focus
  // ring come from the DS, not bespoke CSS. The visible text is the raw label;
  // the accessible name is translatable.
  //
  // The chevron is passed as the main `Icon` (not `trailingIcon`) so LxButton's
  // `shouldUseAriaLabel` stays true and the translated "Back to {label}" name is
  // preserved. LxButton renders the main `Icon` verbatim (only `trailingIcon` is
  // RTL-aware), so we pick the direction-correct chevron here: it points back
  // towards content start — left in LTR, right in RTL.
  const BackChevron = isRtl ? ChevronRight : ChevronLeft;
  const backButtonEl = showBackButton ? (
    <div className={styles.backButtonContainer}>
      <LxButton
        variant="oncolor"
        size="xs"
        text={backButtonLabel}
        Icon={BackChevron}
        iconPosition="start"
        ariaLabel={intl.formatMessage(messages.backTo, {
          label: backButtonLabel,
        })}
        onClickHandler={handleClose}
        testId="modal-back-btn"
      />
    </div>
  ) : null;

  const closeButtonEl =
    showCloseButton && !showBackButton ? (
      <div className={styles.closeButtonContainer}>
        <LxButton
          variant={closeButtonVariant}
          size="xs"
          text={
            closeButtonText
              ? intl.formatMessage({
                  id: closeButtonText,
                  defaultMessage: "Close",
                  description: "Custom close button text for modal",
                })
              : intl.formatMessage(messages.close)
          }
          Icon={CircleX}
          iconPosition="end"
          onClickHandler={handleClose}
          testId="modal-close-btn"
        />
      </div>
    ) : null;

  if (inline) {
    if (!isOpen) return null;
    return (
      <div
        className={styles.inlineContainer}
        data-testid={testId ? `${testId}-container` : undefined}
      >
        <div
          className={combinedClassName}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          data-testid={testId}
        >
          {closeButtonEl}
          <div className={styles.contentContainer}>{content}</div>
        </div>
      </div>
    );
  }

  const overlayClassName = isFullscreen
    ? styles.fullscreenOverlay
    : styles.overlay;

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={overlayClassName}
          style={overlayZIndex != null ? { zIndex: overlayZIndex } : undefined}
          data-testid={testId ? `${testId}-overlay` : undefined}
        >
          <Dialog.Content
            className={combinedClassName}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedBy}
            aria-modal="true"
            data-testid={testId}
            // Optionally keep the dialog open when the user interacts with a
            // sibling overlay (e.g. the glossary popup); close only via the
            // close button or Escape.
            onInteractOutside={
              disableOutsideDismiss
                ? (event) => event.preventDefault()
                : undefined
            }
          >
            {backButtonEl}
            {closeButtonEl}
            <div className={styles.contentContainer}>{content}</div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export const Modal: React.FC<ModalProps> = (props) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <ModalInternal {...props} />
  </TranslationsProvider>
);

export default Modal;
